import {
	resolveTableViewState,
	Table,
	TableCard,
	TableStateRow,
} from "@components";
import type { ReactNode } from "react";
import { cx } from "@/utils/cx";
import {
	EXPENSES_CONSOLIDATED_BY_PLATE_COLUMNS,
	REPORT_SUMMARY_CATEGORY_TEXT_COLORS,
} from "../constants";
import type { ExpenseConsolidatedByPlateItem } from "../types";
import { formatCurrencyOrDash } from "../utils";

type ExpensesConsolidatedByPlateTableProps = {
	data: ExpenseConsolidatedByPlateItem[];
	isLoading?: boolean;
	isError?: boolean;
	onRetry?: () => void;
	footer?: ReactNode;
};

const CONSOLIDATED_CURRENCY_FIELDS = [
	"infraction",
	"maintenance",
	"toll",
	"reimbursement",
	"fuel",
	"trip",
	"other",
	"total",
] as const satisfies readonly (keyof ExpenseConsolidatedByPlateItem)[];

const DENSE_CELL_CLASS = "px-4";

function ExpensesConsolidatedByPlateTableHead({
	column,
}: {
	column: (typeof EXPENSES_CONSOLIDATED_BY_PLATE_COLUMNS)[number];
}) {
	const label = column.label;
	const Icon = column.Icon;
	const iconColor = REPORT_SUMMARY_CATEGORY_TEXT_COLORS[column.type ?? 1];

	return (
		<Table.Head
			className={DENSE_CELL_CLASS}
			id={column.id}
			isRowHeader={EXPENSES_CONSOLIDATED_BY_PLATE_COLUMNS.indexOf(column) === 0}
			key={column.id}
			style={{
				...(!!column?.minWidth && {
					minWidth: `${column.minWidth}px`,
				}),
			}}
		>
			{!!Icon && (
				<Icon aria-hidden className={cx("size-3.5 shrink-0", iconColor)} />
			)}
			<span className="whitespace-nowrap font-semibold text-primary text-xs">
				{label}
			</span>
		</Table.Head>
	);
}

function ExpensesConsolidatedByPlateTableRow({
	item,
}: {
	item: ExpenseConsolidatedByPlateItem;
}) {
	return (
		<Table.Row
			className="last:border-b-0"
			columns={EXPENSES_CONSOLIDATED_BY_PLATE_COLUMNS}
			id={item.vehicleId}
		>
			<Table.Cell
				className={cx(DENSE_CELL_CLASS, "whitespace-nowrap font-semibold")}
			>
				{item.plate}
			</Table.Cell>
			<Table.Cell className={cx(DENSE_CELL_CLASS, "text-tertiary")}>
				{item.model}
			</Table.Cell>
			{CONSOLIDATED_CURRENCY_FIELDS.map((field) => (
				<Table.Cell
					className={cx(
						DENSE_CELL_CLASS,
						"whitespace-nowrap",
						field === "total" && "font-semibold"
					)}
					key={field}
				>
					{formatCurrencyOrDash(item[field])}
				</Table.Cell>
			))}
		</Table.Row>
	);
}

export function ExpensesConsolidatedByPlateTable({
	data,
	isLoading = false,
	isError = false,
	onRetry,
	footer,
}: ExpensesConsolidatedByPlateTableProps) {
	const view = resolveTableViewState({
		itemCount: data.length,
		isLoading,
		isError,
	});

	return (
		<TableCard.Root variant="listing">
			<TableCard.Header className="md:px-4" title="Consolidado por Placa" />
			<Table
				aria-label="Consolidado por Placa"
				isRefreshing={view.isRefreshing}
				selectionMode="none"
			>
				<Table.Header columns={EXPENSES_CONSOLIDATED_BY_PLATE_COLUMNS}>
					{(column) => <ExpensesConsolidatedByPlateTableHead column={column} />}
				</Table.Header>

				<Table.Body>
					{view.view === "data" ? (
						<>
							{view.hasRefreshError ? (
								<TableStateRow
									colSpan={EXPENSES_CONSOLIDATED_BY_PLATE_COLUMNS.length}
									onRetry={onRetry}
									state="refresh-error"
								/>
							) : null}
							{data.map((item) => (
								<ExpensesConsolidatedByPlateTableRow
									item={item}
									key={item.vehicleId}
								/>
							))}
						</>
					) : (
						<TableStateRow
							colSpan={EXPENSES_CONSOLIDATED_BY_PLATE_COLUMNS.length}
							emptyContent="Nenhum veículo encontrado para os filtros selecionados."
							errorMessage="Não foi possível carregar o consolidado por placa."
							loadingLabel="Carregando consolidado por placa"
							onRetry={onRetry}
							state={view.view}
						/>
					)}
				</Table.Body>
			</Table>
			{footer}
		</TableCard.Root>
	);
}
